import React, { useState, useEffect } from 'react'
import { Typography, Card, Table, Tag, Button, Space, Modal, Form, Input, Select, message } from 'antd'
import { UserAddOutlined } from '@ant-design/icons'
import { authAPI } from '../services/api'

const { Title } = Typography

const Admins = () => {
  const [isEditModalVisible, setIsEditModalVisible] = useState(false)
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false)
  const [selectedAdmin, setSelectedAdmin] = useState(null)
  const [admins, setAdmins] = useState([])
  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm()
  const [createForm] = Form.useForm()
  const isMobile = window.innerWidth < 768

  // Fetch admins from API
  useEffect(() => {
    fetchAdmins()
  }, [])

  const fetchAdmins = async () => {
    setLoading(true)
    try {
      const data = await authAPI.listAdmins()

      // Transform API data to match table format
      const transformedData = data.map(admin => ({
        key: admin.admin_id,
        name: admin.full_name || admin.email,
        email: admin.email,
        role: admin.role,
        status: admin.is_active ? 'active' : 'inactive',
        lastLogin: admin.last_login ? new Date(admin.last_login).toLocaleString() : 'Never'
      }))

      setAdmins(transformedData)
    } catch (error) {
      console.error('Failed to fetch admins:', error)
      message.error('Failed to load admins')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (record) => {
    setSelectedAdmin(record)
    form.setFieldsValue({
      name: record.name,
      email: record.email,
      role: record.role,
    })
    setIsEditModalVisible(true)
  }

  const handleEditSubmit = async () => {
    try {
      const values = await form.validateFields()

      // Split name into first_name and last_name
      const nameParts = values.name.trim().split(' ')
      const firstName = nameParts[0] || ''
      const lastName = nameParts.slice(1).join(' ') || ''

      // Call API to update admin
      await authAPI.updateAdmin(selectedAdmin.key, {
        email: values.email,
        first_name: firstName,
        last_name: lastName,
        role: values.role
      })

      message.success('Admin updated successfully')
      setIsEditModalVisible(false)
      form.resetFields()

      // Refresh the admin list
      fetchAdmins()
    } catch (error) {
      console.error('Update failed:', error)
      message.error(error.response?.data?.detail || error.message || 'Failed to update admin')
    }
  }

  const handleCreateSubmit = async () => {
    try {
      const values = await createForm.validateFields()

      await authAPI.createAdmin({
        email: values.email,
        password: values.password,
        full_name: values.full_name,
        role: values.role
      })

      message.success('Admin created successfully')
      setIsCreateModalVisible(false)
      createForm.resetFields()

      // Refresh the admin list
      fetchAdmins()
    } catch (error) {
      console.error('Create failed:', error)
      message.error(error.response?.data?.detail || error.message || 'Failed to create admin')
    }
  }

  const handleDisable = async (record) => {
    Modal.confirm({
      title: `${record.status === 'active' ? 'Disable' : 'Enable'} Admin`,
      content: `Are you sure you want to ${record.status === 'active' ? 'disable' : 'enable'} ${record.name}?`,
      onOk: async () => {
        try {
          await authAPI.toggleAdminStatus(record.key)
          message.success(`Admin ${record.status === 'active' ? 'disabled' : 'enabled'} successfully`)
          fetchAdmins()
        } catch (error) {
          console.error('Toggle status failed:', error)
          message.error(error.response?.data?.detail || 'Failed to toggle admin status')
        }
      }
    })
  }

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <Tag color={role === 'super_admin' ? 'gold' : 'blue'}>
          {role === 'super_admin' ? 'Super Admin' : 'Platform Admin'}
        </Tag>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status}
        </Tag>
      )
    },
    { title: 'Last Login', dataIndex: 'lastLogin', key: 'lastLogin' },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => handleEdit(record)}>Edit</Button>
          <Button size="small" danger onClick={() => handleDisable(record)}>Disable</Button>
        </Space>
      )
    }
  ]

  // Mobile-optimized columns
  const responsiveColumns = isMobile ? columns.filter(col =>
    ['name', 'status', 'actions'].includes(col.key)
  ) : columns

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <Title level={2} className="mb-0">Platform Admins</Title>
        <Button
          type="primary"
          icon={<UserAddOutlined />}
          onClick={() => setIsCreateModalVisible(true)}
          block={isMobile}
        >
          {isMobile ? 'Add Admin' : 'Add Admin'}
        </Button>
      </div>

      <Card>
        <div style={{ overflowX: 'auto' }}>
          <Table
            columns={responsiveColumns}
            dataSource={admins}
            loading={loading}
            rowKey="key"
            scroll={{ x: isMobile ? 600 : undefined }}
            pagination={{
              simple: isMobile,
              showSizeChanger: !isMobile
            }}
          />
        </div>
      </Card>

      <Modal
        title="Edit Admin"
        open={isEditModalVisible}
        onOk={handleEditSubmit}
        onCancel={() => {
          setIsEditModalVisible(false)
          form.resetFields()
        }}
        okText="Save"
        cancelText="Cancel"
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter admin name' }]}
          >
            <Input placeholder="Enter name" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input placeholder="Enter email" />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Please select role' }]}
          >
            <Select>
              <Select.Option value="super_admin">Super Admin</Select.Option>
              <Select.Option value="ops_admin">Operations Admin</Select.Option>
              <Select.Option value="support_admin">Support Admin</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Create Admin Modal */}
      <Modal
        title="Create New Admin"
        open={isCreateModalVisible}
        onOk={handleCreateSubmit}
        onCancel={() => {
          setIsCreateModalVisible(false)
          createForm.resetFields()
        }}
        okText="Create"
        cancelText="Cancel"
      >
        <Form
          form={createForm}
          layout="vertical"
        >
          <Form.Item
            name="full_name"
            label="Full Name"
            rules={[{ required: true, message: 'Please enter full name' }]}
          >
            <Input placeholder="Enter full name" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input placeholder="Enter email" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Please enter password' },
              { min: 8, message: 'Password must be at least 8 characters' }
            ]}
          >
            <Input.Password placeholder="Enter password" />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Please select role' }]}
            initialValue="ops_admin"
          >
            <Select>
              <Select.Option value="super_admin">Super Admin</Select.Option>
              <Select.Option value="ops_admin">Operations Admin</Select.Option>
              <Select.Option value="support_admin">Support Admin</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default Admins
